// ============================================================================
// PROCESSOS CADERNO LIST VIEW
// ARQUIVO: src/features/files/ProcessosCadernoListView.jsx
// RESPONSABILIDADE: Lista plana de todos os autos_workspaces, pesquisável
//   por numeroCNJ/alias/cliente — padrão validado contra tela real da
//   Astrea (título, cliente/pasta, ação/foro, última atualização).
// Fusão Processos/Caderno (MC16): CONSTRUÇÃO NOVA, não porte. Confirmado
// nesta sessão que não existe hoje nenhuma lista global equivalente —
// AutosDashboard.jsx tem um filtro (`filtered`/`groupedFiltered`) que é
// código morto (nunca renderizado), e o `allWorkspaces` que chega em
// CadernoCockpit.jsx só alimenta um seletor de cadernos DO CLIENTE JÁ
// SELECIONADO, não uma lista global. Este componente NÃO importa nem
// referencia CadernoCockpit.jsx de forma alguma — respeita a pausa da
// Fase F.
// ============================================================================
import React, { useMemo, useState } from 'react';
import { saveStatus } from './CadernoCockpitRepository';
import { resolveProcessTitle, normalizeSearchText, filterWorkspacesBySearch, getStatusStyle, toTitleCasePt } from '../../utils/formatters';

// Exportado (MC48, 22/08/2026) para o formulário de criação de caderno em
// AutosDashboard.jsx reusar a mesma lista, em vez de duplicar o literal.
export const STATUS_COLUMNS = ['Ativo', 'Suspenso', 'Recurso', 'Liminar', 'Arquivado'];

const formatUpdatedAt = (w) => {
  const raw = w?.updatedAt;
  const ms = raw?.toDate ? raw.toDate().getTime() : (raw ? new Date(raw).getTime() : 0);
  if (!ms || Number.isNaN(ms)) return '—';
  return new Date(ms).toLocaleDateString('pt-BR');
};

// Fusão Processos/Caderno (MC17): modo Kanban, estrutura copiada de
// KanbanBoard em src/features/processes/ProcessManager.jsx (não importada
// de lá — aquele componente fica intacto, é o mesmo padrão visual já
// validado, só campos trocados: numeroCNJ no lugar de nada, sem
// parteContraria). onStatusChange grava via saveStatus (MC03).
const KanbanView = ({ workspaces, onStatusChange, onSelectWorkspace }) => {
  const handleDragStart = (e, id) => e.dataTransfer.setData('id', id);
  const handleDrop = (e, status) => {
    const id = e.dataTransfer.getData('id');
    if (id) onStatusChange(id, status);
  };

  return (
    <div className="flex gap-4 overflow-x-auto flex-1 p-4 custom-scrollbar h-full">
      {STATUS_COLUMNS.map((status) => (
        <div
          key={status}
          className="min-w-[280px] w-[280px] bg-gray-100/80 rounded-xl flex flex-col border border-gray-200"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => handleDrop(e, status)}
        >
          <div className="p-3 rounded-t-xl border-b border-gray-200 flex justify-between items-center bg-white/50">
            <h3 className="font-bold text-xs uppercase text-gray-700">{status}</h3>
            <span className="bg-white px-2 py-0.5 rounded-full text-[10px] font-bold">
              {workspaces.filter((w) => (w.status || 'Ativo') === status).length}
            </span>
          </div>
          <div className="p-2 flex-1 overflow-y-auto space-y-2 custom-scrollbar">
            {workspaces.filter((w) => (w.status || 'Ativo') === status).map((w) => {
              const { title, subtitle } = resolveProcessTitle(w);
              return (
                <div
                  key={w.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, w.id)}
                  onClick={() => onSelectWorkspace(w)}
                  className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 cursor-grab hover:shadow-md"
                >
                  <div className="flex justify-between mb-1">
                    <span className="text-[10px] font-mono bg-gray-50 px-1 rounded">{subtitle}</span>
                  </div>
                  <h4 className="text-xs font-bold text-gray-800 line-clamp-2">{title}</h4>
                  {w.clientName && <p className="text-[10px] text-gray-500">{toTitleCasePt(w.clientName)}</p>}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

const SORT_ACCESSORS = {
  titulo: (w) => normalizeSearchText(resolveProcessTitle(w).title),
  cliente: (w) => normalizeSearchText(w?.clientName || w?.alias),
  acao: (w) => normalizeSearchText(w?.metadata?.assunto),
  atualizado: (w) => {
    const raw = w?.updatedAt;
    return raw?.toDate ? raw.toDate().getTime() : (raw ? new Date(raw).getTime() : 0);
  },
};

const SortableHeader = ({ field, label, sortField, sortDir, onSort }) => (
  <th
    className="text-left px-4 py-3 cursor-pointer select-none hover:text-gray-700"
    onClick={() => onSort(field)}
  >
    {label}
    {sortField === field && (
      <i className={`fas fa-caret-${sortDir === 'asc' ? 'up' : 'down'} ml-1`}></i>
    )}
  </th>
);

export const ProcessosCadernoListView = ({ workspaces = [], onSelectWorkspace, onCreateNew }) => {
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [sortField, setSortField] = useState('atualizado');
  const [sortDir, setSortDir] = useState('desc');

  const handleStatusChange = async (workspaceId, status) => {
    try {
      await saveStatus(workspaceId, status);
    } catch (error) {
      console.error(error);
      alert('Erro ao mudar status: ' + String(error?.message || error));
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    const base = filterWorkspacesBySearch(workspaces, search);

    const accessor = SORT_ACCESSORS[sortField] || SORT_ACCESSORS.atualizado;
    const sorted = [...base].sort((a, b) => {
      const va = accessor(a);
      const vb = accessor(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [workspaces, search, sortField, sortDir]);

  return (
    <div className="flex-1 h-full overflow-hidden flex flex-col bg-white">
      <div className="p-4 border-b border-gray-200 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por CNJ, cliente ou apelido do caderno..."
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm focus:border-brand-blue outline-none"
          />
        </div>
        <span className="text-xs text-gray-400">{filtered.length} de {workspaces.length}</span>
        <div className="flex-1"></div>
        <div className="flex rounded-lg border border-gray-300 overflow-hidden">
          <button
            onClick={() => setViewMode('list')}
            className={`px-3 py-1.5 text-[10px] font-bold uppercase ${viewMode === 'list' ? 'bg-brand-blue text-white' : 'bg-white text-gray-500'}`}
          >
            <i className="fas fa-list mr-1"></i>Lista
          </button>
          <button
            onClick={() => setViewMode('kanban')}
            className={`px-3 py-1.5 text-[10px] font-bold uppercase ${viewMode === 'kanban' ? 'bg-brand-blue text-white' : 'bg-white text-gray-500'}`}
          >
            <i className="fas fa-columns mr-1"></i>Kanban
          </button>
        </div>
        <button
          onClick={onCreateNew}
          className="bg-brand-blue text-white text-xs font-bold uppercase px-4 py-2 rounded-lg hover:bg-brand-blue-darker flex items-center gap-2"
        >
          <i className="fas fa-plus"></i> Novo Processo/Caso
        </button>
      </div>

      {viewMode === 'kanban' ? (
        <KanbanView workspaces={filtered} onStatusChange={handleStatusChange} onSelectWorkspace={onSelectWorkspace} />
      ) : (
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <table className="w-full text-sm table-fixed">
          <colgroup>
            <col className="w-[38%]" />
            <col className="w-[22%]" />
            <col className="w-[26%]" />
            <col className="w-[14%]" />
          </colgroup>
          <thead className="sticky top-0 bg-gray-50 border-b border-gray-200 text-[11px] uppercase text-gray-500 font-bold tracking-wide">
            <tr>
              <SortableHeader field="titulo" label="Título" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <SortableHeader field="cliente" label="Cliente" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <SortableHeader field="acao" label="Ação / Foro" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              <SortableHeader field="atualizado" label="Última atualização" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
            </tr>
          </thead>
          <tbody>
            {filtered.map((w) => {
              // MC99 (10/09/2026): pedido do titular — portar pra esta
              // tabela os mesmos padrões já fechados na árvore lateral de
              // "Processos" (CadernoCockpit.jsx, MC81-98): (1) identificador
              // (número CNJ ou "Pré-Processual") na própria linha, separado
              // do nome das partes — mesma lógica de split, mesmo
              // `tabular-nums` em vez de `font-mono` (evita "fontes
              // diferentes", achado do MC90); (2) status como bolinha
              // colorida + rótulo por extenso (`getStatusStyle`, mesma cor
              // por status da árvore/aba Vínculos) no lugar da pílula cinza
              // única (que nem mudava de cor por status — "Ativo" e
              // "Arquivado" saíam idênticos).
              // MC99b (10/09/2026): achado do titular corrigindo a MC99
              // original — a tabela tem 4 colunas dividindo a largura, não
              // "espaço de sobra"; nome das partes em 15px/negrito quebrava
              // linha demais. Reduzido pra 12px/semibold, igual à árvore
              // (ver MC100 abaixo pro resto da padronização de fonte nas
              // outras colunas).
              const { title, isPreProcessual, numeroFormatted } = resolveProcessTitle(w);
              const identifier = isPreProcessual ? 'Pré-Processual' : (numeroFormatted || '');
              const namesPart = isPreProcessual ? title : title.slice(identifier.length + 3);
              // Fallback padronizado (13/09/2026): era 'Sem status', divergente dos outros
              // 2 lugares que leem o mesmo campo w.status (aba Resumo em CadernoCockpit.jsx
              // ~L1542/L4424, e o card-resumo do Cockpit ~L6114) — os dois já usavam 'Ativo'.
              // Mesmo workspace sem status gravado mostrava rótulo diferente dependendo de
              // qual tela você estava olhando. STATUS_COLUMNS[0] = 'Ativo' é o valor
              // canônico/default do domínio (achado do titular, comparando as duas telas).
              const statusLabel = String(w?.status || 'Ativo').trim();
              const statusStyle = getStatusStyle(statusLabel);
              const acao = String(w?.metadata?.assunto || '').trim();
              const foro = [w?.juizo, w?.comarca].filter(Boolean).join(' · ');
              // MC100 (10/09/2026): 2º achado do titular na mesma rodada —
              // testei suprimir a "Pasta" só quando o alias fosse IDÊNTICO
              // ao título (MC anterior), mas na prática o alias real de
              // registro que já tem CNJ hoje quase sempre vem de uma versão
              // ANTIGA do título, de quando o caderno ainda era
              // pré-processual (ex.: "Fulano vs. Beltrana (Pré-Processual)"
              // sobrevivendo depois que um número real foi vinculado) —
              // diferente o suficiente pra escapar da comparação exata, mas
              // sem informação nova nenhuma, só o nome do cliente/partes
              // repetido pela 3ª vez na mesma linha (Título + Cliente +
              // "Pasta"), às vezes com um "(Pré-Processual)" desatualizado
              // e contraditório junto. Não existe, na base real, um caso
              // onde `alias` carregue um apelido genuinamente distinto do
              // que já está no Título — a coluna "Pasta" não tinha
              // conteúdo confiável pra mostrar. Removida a coluna
              // conceitualmente (ficou só "Cliente"); se um dia existir
              // apelido de caderno de verdade, é a Título (que já usa o
              // alias como identidade no ramo pré-processual) que deve
              // continuar sendo a fonte, não uma 2ª linha solta aqui.
              // Also (mesmo achado): fontes/cores "apagadas" — os campos
              // secundários usavam `text-gray-400` (viola o piso de
              // contraste 4.5:1, item 3 do checklist de acessibilidade) e
              // cada coluna tinha um tamanho de fonte diferente sem critério
              // (14px aqui, 12px ali). Padronizado em 2 níveis, iguais em
              // toda coluna: TIER1 (conteúdo principal) = `text-xs
              // font-semibold text-gray-800`; TIER2 (informação de apoio,
              // ex.: foro/data) = `text-xs font-medium text-gray-600` — só
              // a coluna Título mantém o tom azul-marca já aprovado
              // (identidade do processo, não é "conteúdo genérico").
              return (
                <tr
                  key={w.id}
                  onClick={() => onSelectWorkspace?.(w)}
                  className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer align-top"
                >
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-1.5">
                      <span
                        style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                        className="text-xs font-semibold text-[#20243B]"
                        title={identifier}
                      >
                        {identifier}
                      </span>
                      <span
                        className="ml-auto shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 whitespace-nowrap"
                        title={statusLabel}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${statusStyle.dot}`} aria-hidden="true" />
                        {statusLabel}
                      </span>
                      {/* MC42c (Fase F2): abre direto no caderno, aba Resumo
                          (mesmo destino do clique na linha — activeTab já
                          reseta pra "resumo" ao trocar de workspace, MC41j).
                          Afordance explícita, mesmo padrão da Astrea. */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectWorkspace?.(w);
                        }}
                        title="Editar"
                        className="shrink-0 text-gray-300 hover:text-[#C5A059] transition-colors"
                      >
                        <i className="fas fa-pencil-alt text-xs"></i>
                      </button>
                    </div>
                    <div className="mt-1">
                      <span
                        className="font-semibold text-xs text-[#20243B] leading-snug line-clamp-2"
                        title={namesPart}
                      >
                        {namesPart}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3.5">
                    <div
                      className="font-semibold text-xs text-gray-800 leading-snug line-clamp-2"
                      title={w?.clientName ? toTitleCasePt(w.clientName) : '(sem cliente)'}
                    >
                      {w?.clientName ? toTitleCasePt(w.clientName) : '(sem cliente)'}
                    </div>
                  </td>
                  <td className="px-4 py-3.5">
                    <div
                      className="font-semibold text-xs text-gray-800 leading-snug line-clamp-2"
                      title={acao || '—'}
                    >
                      {acao || '—'}
                    </div>
                    {foro && (
                      <div
                        className="font-medium text-xs text-gray-600 leading-snug line-clamp-2 mt-0.5"
                        title={foro}
                      >
                        {foro}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3.5 font-medium text-xs text-gray-600">{formatUpdatedAt(w)}</td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={4} className="p-8 text-center text-xs text-gray-400">Nenhum caderno encontrado.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
};
